import axios from 'axios';
import utilsStorage from '@/utils/storage';

let configCache = null;
let configPromise = null;

async function loadConfig() {
  if (configCache) return configCache;

  if (!configPromise) {
    configPromise = fetch('/config.json')
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');

        return response.json();
      })
      .then((data) => {
        configCache = data;

        return data;
      })
      .catch(() => {
        configPromise = null;

        return null;
      });
  }

  return configPromise;
}

const OFFLINE_MESSAGE =
  'Seu computador está sem internet. Confira a conexão e tente novamente.';

const SERVER_UNREACHABLE_MESSAGE =
  'Não foi possível se conectar com o servidor. Se essa mensagem persistir, por favor, contate o suporte técnico.';

/* Sem resposta do servidor: quando o navegador sabe que está offline, o
   problema é a internet do usuário, não o sistema. */
export function describeConnectionError() {
  const message = navigator.onLine === false ? OFFLINE_MESSAGE : SERVER_UNREACHABLE_MESSAGE;

  return `${message} (Ref.: ${CONNECTION_REFERENCE})`;
}

const CONNECTION_REFERENCE = 'SEM-CONEXAO';
const REFERENCE_PATTERN = /\(Ref\.:/;
const CORRELATION_REFERENCE_LENGTH = 8;
const MESSAGE_FIELDS = ['mensagem', 'message', 'error'];

function _responseReference(data, status) {
  return data?.errorCode || String(data?.correlationId || '').slice(0, CORRELATION_REFERENCE_LENGTH) || `HTTP-${status}`;
}

/* Toda mensagem de erro do servidor chega à tela com o código para o suporte.
   O errorParser do backend já acrescenta "(Ref.: …)"; respostas fora do padrão
   (campo message/error, sem código) recebem aqui o código ou o status HTTP. */
export function ensureReferenceOnErrorResponse(error) {
  const data = error?.response?.data;

  if (!data || typeof data !== 'object') return error;

  const field = MESSAGE_FIELDS.find((name) => typeof data[name] === 'string' && data[name].trim());

  if (!field || REFERENCE_PATTERN.test(data[field])) return error;

  const withReference = `${data[field].trim().replace(/\.$/, '')}. (Ref.: ${_responseReference(data, error.response.status)})`;

  data[field] = withReference;

  if (!data.mensagem) data.mensagem = withReference;

  return error;
}

async function getApi() {
  const config = await loadConfig();

  const api = axios.create({
    baseURL: config?.urlApi
  });

  api.interceptors.request.use(async (config) => {
    const token = await utilsStorage.getTokenOnStorage();
    config.headers.Authorization = token;

    return config;
  });

  const isPublicRoute = () => {
    const currentPath = window.location.pathname;
    // Precisa espelhar as rotas com meta.requireAuth === false no router:
    // sem isso um 401 de fundo derruba a página pública para o /login.
    const publicPaths = ['/login', '/assinatura', '/confirmacao-assinatura', '/ui'];

    return publicPaths.some(path => currentPath.includes(path));
  };

  api.interceptors.response.use(
    (response) => {
      if (response.status === 401 && response.config.url !== '/login' && !isPublicRoute()) {
        utilsStorage.removeAuth();

        setTimeout(() => {
          // Reverifica AQUI, não só quando o 401 chegou: entre a resposta e
          // este disparo o usuário pode ter ido para uma rota pública, e o
          // redirect pendente o arrancava dela.
          if (isPublicRoute()) return;

          // Guarda onde o usuario estava para o login devolve-lo la (ex.: /suporte).
          utilsStorage.saveCurrentLocationAsReturnPath();
          window.location.href = '/login';
        }, 1000);
      }

      return response;
    },
    (error) => {
      if (!error.response) return Promise.reject(describeConnectionError());

      const isUnauthorized = error.response.status === 401;
      const isLoginRoute = error.response.config.url === '/login';
      const hasValidBaseUrl = !!error.response.config.baseURL;

      if (isUnauthorized && !isLoginRoute && !isPublicRoute() && hasValidBaseUrl) {
        utilsStorage.removeAuth();

        setTimeout(() => {
          // Reverifica AQUI, não só quando o 401 chegou: entre a resposta e
          // este disparo o usuário pode ter ido para uma rota pública, e o
          // redirect pendente o arrancava dela.
          if (isPublicRoute()) return;

          // Guarda onde o usuario estava para o login devolve-lo la (ex.: /suporte).
          utilsStorage.saveCurrentLocationAsReturnPath();
          window.location.href = '/login';
        }, 3000);

        return Promise.reject(
          `Seu login de acesso expirou, efetue o login novamente para acessar o sistema.`
        );
      }

      return Promise.reject(ensureReferenceOnErrorResponse(error));
    }
  );

  return api;
}

export default getApi;
